import { ClientTeamPerson, ClientTeamRow } from "./clientTeam.types";

/** The owner's words for the ⓘ button (2026-10-31). */
export const CLIENT_TEAM_INFO = {
  manager:
    "Client manager: gets new work for this client and its day-to-day notifications (uploads, replies, signatures, comments).",
  owner:
    "Client owner: responsible for the relationship; only told when something goes wrong (work overdue or stuck, invoice 30+ days late).",
  both: "Both always have access to the client. One person can be both.",
};

export const CLIENT_TEAM_INFO_TEXT = [
  CLIENT_TEAM_INFO.manager,
  CLIENT_TEAM_INFO.owner,
  CLIENT_TEAM_INFO.both,
].join(" ");

/** "Me" choice in the people filters. */
export const ME = "me";

/**
 * A client id that never exists: sent as the Work page's client filter when the
 * Client owner / manager filter matches no client, so the list is empty instead
 * of unfiltered (an empty client list means "all clients" to find-work).
 */
export const NO_CLIENT_MATCH = "00000000-0000-0000-0000-000000000000";

const same = (a?: string | null, b?: string | null) =>
  !!a && !!b && a.toLowerCase() === b.toLowerCase();

/** Picked people with "me" turned into the signed-in person's id, no repeats. */
export const resolvePeople = (picked: string[], meId?: string | null) => {
  const out: string[] = [];
  picked.forEach((value) => {
    const id = value === ME ? meId || "" : value;
    if (id && !out.some((x) => same(x, id))) out.push(id);
  });
  return out;
};

export const teamFilterActive = (owners: string[], managers: string[]) =>
  owners.length > 0 || managers.length > 0;

/**
 * The client filter actually sent to find-work: without a Client owner /
 * manager filter, the picked clients; with one, the clients it matched
 * (intersected with the picked clients, if any). Nothing matched = the
 * never-matching id, so the list is empty.
 */
export const effectiveClientIds = (
  pickedClientIds: string[],
  teamActive: boolean,
  teamClientIds: string[] | undefined,
): string[] => {
  if (!teamActive) return pickedClientIds;
  const team = teamClientIds ?? [];
  const result = pickedClientIds.length
    ? pickedClientIds.filter((id) => team.some((t) => same(t, id)))
    : team;
  return result.length ? result : [NO_CLIENT_MATCH];
};

/** An active (not archived) person, or null. */
export const activePerson = (person?: ClientTeamPerson | null) =>
  person && !person.archived ? person : null;

/**
 * Client manager for the New work window: the manager every picked client
 * shares (active people only). Different managers, or a client without one =
 * none (the window keeps its usual default).
 */
export const sharedManager = (
  rows: ClientTeamRow[] | undefined,
  customerIds: string[],
): ClientTeamPerson | null => {
  if (!rows || !customerIds.length) return null;
  let found: ClientTeamPerson | null = null;
  for (const id of customerIds) {
    const row = rows.find((r) => same(r.customerId, id));
    const manager = activePerson(row?.manager);
    if (!manager) return null;
    if (found && !same(found.id, manager.id)) return null;
    found = manager;
  }
  return found;
};

export type DefaultAssigneeSource =
  | "workType"
  | "clientManager"
  | "role"
  | "me"
  | "none";

/**
 * Who the New work window assigns by default (owner 2026-10-31): the work
 * type's own default assignee wins; then the client manager; then, as before,
 * nobody when the work type has a default role (it goes to the role's queue),
 * else the person creating it.
 */
export const chooseDefaultAssignee = (input: {
  workTypeAssigneeId?: string | null;
  hasDefaultRole?: boolean;
  clientManagerId?: string | null;
  meId?: string | null;
}): { id: string | null; source: DefaultAssigneeSource } => {
  if (input.workTypeAssigneeId)
    return { id: input.workTypeAssigneeId, source: "workType" };
  if (input.clientManagerId)
    return { id: input.clientManagerId, source: "clientManager" };
  if (input.hasDefaultRole) return { id: null, source: "role" };
  if (input.meId) return { id: input.meId, source: "me" };
  return { id: null, source: "none" };
};

/** Name shown in lists: "Not set", or the name (+ " (archived)"). */
export const personLabel = (person?: ClientTeamPerson | null) =>
  !person
    ? "Not set"
    : person.archived
    ? `${person.name} (archived)`
    : person.name;

export const personInitials = (name?: string | null) => {
  const parts = (name || "").trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "?";
  const first = parts[0][0] || "";
  const last = parts.length > 1 ? parts[parts.length - 1][0] || "" : "";
  return (first + last).toUpperCase();
};

/** Bulk window: what will happen, in one sentence. */
export const bulkSummary = (
  count: number,
  owner: { set: boolean; name?: string | null },
  manager: { set: boolean; name?: string | null },
) => {
  const clients = count === 1 ? "1 client" : `${count} clients`;
  const parts: string[] = [];
  if (owner.set)
    parts.push(
      owner.name ? `client owner: ${owner.name}` : "remove the client owner",
    );
  if (manager.set)
    parts.push(
      manager.name
        ? `client manager: ${manager.name}`
        : "remove the client manager",
    );
  if (!parts.length) return "Choose what to change.";
  return `For ${clients}: ${parts.join(", ")}.`;
};
